/**
 * `/desks/new`: Add a team, as the sheet in wide mode over Waiting on you.
 *
 * Declared BEFORE `/desks/:deskId` so "new" is never read as a Team id. The
 * list behind it is the inbox, so the page reads as the queue with no Team
 * selected; the sheet opens wide because the backlog picker needs the room,
 * and the rail folds to its strip.
 *
 * Cancel, Close and Escape leave the way the reader came. A row opened on the
 * inbox behind takes the sheet, and the address follows it to `/waiting`.
 *
 * Creating a Team is gated on `create-team` (permissions.ts), which every
 * workspace member has. A role without it lands on Waiting on you; the server
 * is the real gate, and this is the page not pretending otherwise.
 */
import { useCallback, useEffect, useMemo, useRef } from "react";
import { Navigate, useLocation, useNavigate, useSearchParams } from "react-router-dom";

import { useAuthSession, useCan } from "../hooks/useWorkspaceRole.js";
import { useOwnedSheet } from "../shell/useOwnedSheet.js";
import { WaitingRoute } from "../work/WaitingRoute.js";
import { AddTeamSheet } from "./AddTeamSheet.js";
import { parseTeamSize, readsFromSizeParam } from "./createTeamSize.js";

export function CreateTeamRoute() {
  // Standalone mounts (no session) keep the page so the flow stays testable.
  const canCreate = useCan("create-team", true);
  if (!canCreate) return <Navigate to="/waiting" replace />;
  return <AddTeam />;
}

function AddTeam() {
  const session = useAuthSession();
  const canConnect = useCan("manage-connections", true);
  const navigate = useNavigate();
  const location = useLocation();
  const [params] = useSearchParams();
  const sizeParam = params.get("size");
  // The first answers, read once: the GitHub install return carries the size.
  const initial = useMemo(
    () => ({ size: parseTeamSize(sizeParam), reads: readsFromSizeParam(sizeParam) ?? "filter" }),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- the sheet starts from the address it opened on
    [],
  );
  const viewer = session?.user ? { id: session.user.id, displayName: session.user.displayName } : null;

  // A first entry (a link, a reload) has nowhere to go back to.
  const canGoBack = location.key !== "default";
  const leave = useCallback(() => {
    if (canGoBack) navigate(-1);
    else navigate("/waiting", { replace: true });
  }, [canGoBack, navigate]);

  const search = useRef(location.search);
  search.current = location.search;
  const alive = useRef(true);
  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
    };
  }, []);

  // The sheet closed without this page asking. A route change (Create landing
  // on the new Team, Cancel going back) closes it too, and then this page is
  // on its way out, so the close waits a tick and asks first.
  const onClosed = useCallback(() => {
    setTimeout(() => {
      if (!alive.current) return;
      // Another record took the sheet (a row on the inbox behind): keep it open there.
      const item = new URLSearchParams(search.current).get("item");
      if (item) navigate(`/waiting?item=${encodeURIComponent(item)}`, { replace: true });
      else leave();
    }, 0);
  }, [leave, navigate]);

  useOwnedSheet(
    <AddTeamSheet
      viewer={viewer}
      canConnect={canConnect}
      initialSize={initial.size}
      initialReads={initial.reads}
      onCancel={leave}
    />,
    onClosed,
    { wide: true },
  );

  return <WaitingRoute />;
}
